import { z } from "zod";

/** Valid task status values. Must match the taskStatusEnum in the database schema. */
export const taskStatuses = ["todo", "in_progress", "done"] as const;
export const taskStatusSchema = z.enum(taskStatuses);

/** Schema for creating a new task. */
export const createTaskSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title must be 255 characters or less"),
  description: z.string().max(10000, "Description must be 10,000 characters or less").optional(),
  status: taskStatusSchema.default("todo"),
});

/** Schema for updating an existing task. Requires task ID and at least one field to update. */
export const updateTaskSchema = z
  .object({
    id: z.string().uuid("Invalid task ID"),
    title: z
      .string()
      .min(1, "Title is required")
      .max(255, "Title must be 255 characters or less")
      .optional(),
    description: z.string().max(10000, "Description must be 10,000 characters or less").optional(),
    status: taskStatusSchema.optional(),
  })
  .refine(
    (data) =>
      data.title !== undefined || data.description !== undefined || data.status !== undefined,
    {
      message: "At least one field must be provided",
    },
  );

/** Schema for deleting a task. */
export const deleteTaskSchema = z.object({
  id: z.string().uuid("Invalid task ID"),
});

/** Schema for filtering tasks by status. */
export const taskFilterSchema = z.object({
  status: taskStatusSchema.optional(),
});

/** Inferred types. */
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
export type DeleteTaskInput = z.infer<typeof deleteTaskSchema>;
export type TaskFilter = z.infer<typeof taskFilterSchema>;
export type TaskStatus = z.infer<typeof taskStatusSchema>;
